// ============================================================================
// OPTIMIZED SIDEBAR - With HR Manual Leave Entry
// Path: frontend/src/components/dashboard/Sidebar.tsx
// 
// NEW: Added HR Manual Leave Entry under Leave Management
// ============================================================================

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Squares2X2Icon,
  UsersIcon,
  ClockIcon,
  CreditCardIcon,
  ChartBarIcon,
  MapPinIcon,
  CalendarDaysIcon,
  CogIcon,
  UserGroupIcon,
  RectangleGroupIcon,
  UserPlusIcon,
  ClockIcon as OvertimeIcon,
  DocumentTextIcon,
  DevicePhoneMobileIcon,
  CalendarIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  DocumentChartBarIcon,
  BuildingOfficeIcon,
  QrCodeIcon,
  ShieldExclamationIcon,
  ClipboardDocumentCheckIcon,
  MoonIcon,
  CheckBadgeIcon,
  TableCellsIcon,
  CircleStackIcon,
  PencilSquareIcon  // NEW: For HR Manual Leave Entry
} from '@heroicons/react/24/outline';
import styles from './Sidebar.module.css';
import { MegaphoneIcon } from '@heroicons/react/24/outline';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface SidebarProps {
  activePage: string;
  onPageChange: (pageId: string) => void;
  isTransitioning?: boolean;
  onToggle?: (collapsed: boolean) => void;
  user?: any;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode | string;
  isImage: boolean;
  hasSubmenu: boolean;
  submenu?: SubMenuItem[];
}

interface SubMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode | string;
  isImage: boolean;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const Sidebar: React.FC<SidebarProps> = React.memo(({ 
  activePage, 
  onPageChange, 
  isTransitioning = false,
  onToggle,
  user
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  // ============================================================================
  // MEMOIZED MENU ITEMS WITH HR MANUAL LEAVE ENTRY
  // ============================================================================

  const isUserAdmin = useMemo(() => {
    return user?.isAdmin === true;
  }, [user?.isAdmin]);

  const menuItems = useMemo((): MenuItem[] => {
    const baseMenuItems: MenuItem[] = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: <Squares2X2Icon className={styles.heroIcon} />,
        isImage: false,
        hasSubmenu: false
      },
      {
        id: 'phoenician',
        label: 'Phoenician',
        icon: '/images/photos.png',
        isImage: true,
        hasSubmenu: true,
        submenu: [
          {
            id: 'employees',
            label: 'Manage Employees',
            icon: <UsersIcon className={styles.heroIcon} />,
            isImage: false
          },
          
          {
            id: 'Work-timing',
            label: 'Work-Timing for Employees',
            icon: <CalendarIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'manpower-allocation',
            label: 'Manpower Allocation',
            icon: <BuildingOfficeIcon className={styles.heroIcon} />,
            isImage: false
          }
        ]
      },
      
      
      {
        id: 'reports',
        label: 'Reports and Analytics',
        icon: <ChartBarIcon className={styles.heroIcon} />,
        isImage: false,
        hasSubmenu: true,
        submenu: [
          
          {
            id: 'security-audit-logs',
            label: 'Security Audit Logs',
            icon: <ShieldExclamationIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'data-accuracy-report',
            label: 'Data Accuracy Report',
            icon: <CircleStackIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
      id: 'checkout-audit-logs',
      label: 'Checkout Audit Logs',
      icon: <ClipboardDocumentCheckIcon className={styles.heroIcon} />,
      isImage: false
    }
        ]
      },
      {
        id: 'location',
        label: 'Manage Location',
        icon: <MapPinIcon className={styles.heroIcon} />,
        isImage: false,
        hasSubmenu: false
      },
      {
        id: 'leave',
        label: 'Leave Management',
        icon: <CalendarDaysIcon className={styles.heroIcon} />,
        isImage: false,
        hasSubmenu: true,
        submenu: [
          // NEW: HR Manual Leave Entry added here
          {
            id: 'hr-manual-leave-entry',
            label: 'HR Manual Leave Entry',
            icon: <PencilSquareIcon className={styles.heroIcon} />,
            isImage: false
          },
          
          {
            id: 'leave-report',
            label: 'Detailed Leaves Report',
            icon: <DocumentChartBarIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'annual-leave-management',
            label: 'Annual Leave Management',
            icon: <TableCellsIcon className={styles.heroIcon} />,
            isImage: false
          },
        ]
      },
      {
        id: 'system-settings',
        label: 'System Settings',
        icon: <CogIcon className={styles.heroIcon} />,
        isImage: false,
        hasSubmenu: true,
        submenu: [
          {
            id: 'payroll-groups',
            label: 'Payroll Groups',
            icon: <UserGroupIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'project-management',
            label: 'Project Management',
            icon: <BuildingOfficeIcon className={styles.heroIcon} />,
            isImage: false  
          },
          {
            id: 'etisalat-sim-management',
            label: 'Etisalat SIM Management',
            icon: <DevicePhoneMobileIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'qr-code-generator',
            label: 'QR Code Generator',
            icon: <QrCodeIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'location-exemptions',
            label: 'Location Exemptions',
            icon: <MapPinIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'group-arrangement',
            label: 'Group Arrangement',
            icon: <RectangleGroupIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'line-managers',
            label: 'Line Managers',
            icon: <UserPlusIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'overtime-access',
            label: 'Overtime Access',
            icon: <OvertimeIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'mastersheet',
            label: 'Mastersheet',
            icon: <DocumentTextIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'night-shift-management',
            label: 'Night Shift Settings',
            icon: <MoonIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
            id: 'register-user',
            label: 'Register User for App',
            icon: <DevicePhoneMobileIcon className={styles.heroIcon} />,
            isImage: false
          }
        ]
      }
    ];

    // Add messaging menu only for admins
    if (isUserAdmin) {
      const messagingMenu: MenuItem = {
        id: 'messaging',
        label: 'Messaging and Alerts',
        icon: <ChatBubbleLeftRightIcon className={styles.heroIcon} />,
        isImage: false,
        hasSubmenu: true,
        submenu: [
          {
            id: 'notifications',
            label: 'Send Notifications to Phoenician Employees',
            icon: <BellIcon className={styles.heroIcon} />,
            isImage: false
          },
          {
  id: 'app-announcements',
  label: 'App Announcements',
  icon: <MegaphoneIcon className={styles.heroIcon} />,
  isImage: false
}
        ]
      };

      // Insert after attendance (index 2)
      baseMenuItems.splice(3, 0, messagingMenu);
    }

    return baseMenuItems;
  }, [isUserAdmin]);

  // ============================================================================
  // CALLBACK FUNCTIONS
  // ============================================================================

  const toggleSidebar = useCallback(() => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    
    if (onToggle) {
      onToggle(newCollapsedState);
    }
  }, [isCollapsed, onToggle]);

  const toggleSubmenu = useCallback((menuId: string) => {
    setExpandedMenus(prev => {
      if (prev.includes(menuId)) {
        return prev.filter(id => id !== menuId);
      } else {
        return [...prev, menuId];
      }
    });
  }, []);

  const handleItemClick = useCallback((itemId: string, hasSubmenu: boolean, submenu?: SubMenuItem[]) => {
    if (isTransitioning) {
      return;
    }

    if (hasSubmenu) {
      // Toggle submenu
      toggleSubmenu(itemId);
      
      // Auto-navigate to first submenu item
      if (submenu && submenu.length > 0) {
        const firstSubmenuId = submenu[0].id;
        if (activePage !== firstSubmenuId) {
          onPageChange(firstSubmenuId);
        }
      }
    } else {
      // Regular page navigation
      if (activePage !== itemId) {
        onPageChange(itemId);
      }
    }
  }, [activePage, onPageChange, isTransitioning, toggleSubmenu]);

  const handleSubmenuClick = useCallback((submenuId: string) => {
    if (isTransitioning || activePage === submenuId) {
      return;
    }

    onPageChange(submenuId);
  }, [activePage, onPageChange, isTransitioning]);

  const isItemActive = useCallback((item: MenuItem) => {
    if (activePage === item.id) {
      return true;
    }
    
    if (item.hasSubmenu && item.submenu) {
      return item.submenu.some((sub: SubMenuItem) => activePage === sub.id);
    }
    
    return false;
  }, [activePage]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderIcon = useCallback((icon: React.ReactNode | string, isImage: boolean, label: string) => {
    if (isImage) {
      return (
        <img 
          src={`${process.env.PUBLIC_URL}${icon}`}
          alt={label}
          className={styles.iconImage}
          loading="lazy"
        />
      );
    }
    return icon;
  }, []);

  const renderMenuItem = useCallback((item: MenuItem) => {
    const isActive = isItemActive(item);
    const isExpanded = expandedMenus.includes(item.id);
    
    return (
      <li key={item.id}>
        <button
          className={`
            ${styles.navItem} 
            ${isActive ? styles.active : ''} 
            ${item.hasSubmenu ? styles.hasSubmenu : ''}
            ${isTransitioning ? styles.transitioning : ''}
          `.trim()}
          aria-label={item.label}
          onClick={() => handleItemClick(item.id, item.hasSubmenu, item.submenu)}
          disabled={isTransitioning}
        >
          <span className={styles.navIcon}>
            {renderIcon(item.icon, item.isImage, item.label)}
          </span>
          <span className={styles.navLabel}>{item.label}</span>
          {item.hasSubmenu && !isCollapsed && (
            <span className={`${styles.expandIcon} ${
              isExpanded ? styles.expanded : ''
            }`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <polyline points="6,9 12,15 18,9" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </span>
          )}
        </button>

        {item.hasSubmenu && item.submenu && isExpanded && !isCollapsed && (
          <ul className={styles.submenuList}>
            {item.submenu.map((subItem) => (
              <li key={subItem.id}>
                <button
                  className={`${styles.submenuItem} ${
                    activePage === subItem.id ? styles.active : ''
                  } ${isTransitioning ? styles.transitioning : ''}`}
                  onClick={() => handleSubmenuClick(subItem.id)}
                  disabled={isTransitioning}
                >
                  <span className={styles.navIcon}>
                    {renderIcon(subItem.icon, subItem.isImage, subItem.label)}
                  </span>
                  <span className={styles.navLabel}>{subItem.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }, [
    isItemActive,
    expandedMenus,
    isCollapsed,
    isTransitioning,
    handleItemClick,
    handleSubmenuClick,
    activePage,
    renderIcon
  ]);

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <aside className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''} ${isTransitioning ? styles.transitioning : ''}`}>
      <div className={styles.sidebarHeader}>
        <button 
          className={styles.toggleButton}
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
          disabled={isTransitioning}  
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2"/>
            <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2"/>
            <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </button>
      </div>

      <nav className={styles.nav}>
        <ul className={styles.navList}>
          {menuItems.map(renderMenuItem)}
        </ul>
      </nav>
    </aside>
  );
});

// ============================================================================
// EXPORT WITH DISPLAY NAME
// ============================================================================

Sidebar.displayName = 'Sidebar';

export default Sidebar;